import { Token, Tokens } from "./tokens.js";
export class Marker {
    /**
     * Serialize a Token.
     *
     * The function must handle whitespaces and comments that the Token
     * contains.
     */
    serializeToken(token, node) {
        return token.serialize();
    }
    /**
     * Serialize a Value.
     */
    serializeValue(prefix, value, suffix, node) {
        return prefix + value + suffix;
    }
    /**
     * Serialize a typename or a groupname
     */
    serializeName(name, node) {
        return name;
    }
    /**
     * Wrapping markup for a node as a whole if needed
     */
    markupFor(node) {
        return [null, null];
    }
}
/**
 * Abstract base class for all nodes in the abstract syntax tree.
 */
export class CDDLNode {
    parentNode = null;
    serialize(marker) {
        // Make sure that parentNode relationships are properly set
        this.setChildrenParent();
        if (marker) {
            const markup = marker.markupFor(this);
            let output = markup[0] !== null ? markup[0] : "";
            output += this._serialize(marker);
            output += markup[1] !== null ? markup[1] : "";
            return output;
        }
        return this._serialize();
    }
    setChildrenParent() {
        /**
         * Initialize the parentNode links from children nodes to this node
         * so that marker can access and adapt its behavior based on the
         * current context.
         */
        for (const child of this.getChildren()) {
            child.parentNode = this;
            child.setChildrenParent();
        }
    }
    getChildren() {
        /**
         * Return the list of children nodes attached to this node
         */
        return [];
    }
    _serializeToken(token, marker) {
        if (!token) {
            return "";
        }
        if (!marker) {
            return token.serialize();
        }
        return marker.serializeToken(token, this);
    }
    toString(indent = 0) {
        return "  ".repeat(indent) + this.constructor.name;
    }
    toJSON() {
        return {};
    }
}
/**
 * A wrapped node is a node optionally enclosed in an open and close token.
 */
export class WrappedNode extends CDDLNode {
    openToken = null;
    closeToken = null;
    serialize(marker) {
        let output = "";
        if (marker) {
            const markup = marker.markupFor(this);
            output += markup[0] !== null ? markup[0] : "";
        }
        output += this._serializeToken(this.openToken, marker);
        output += this._serialize(marker);
        output += this._serializeToken(this.closeToken, marker);
        if (marker) {
            const markup = marker.markupFor(this);
            output += markup[1] !== null ? markup[1] : "";
        }
        return output;
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        if (this.openToken) {
            res.push(this.openToken.toString(indent + 1));
        }
        if (this.closeToken) {
            res.push(this.closeToken.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            openToken: this.openToken?.toJSON(),
            closeToken: this.closeToken?.toJSON()
        });
    }
}
/**
 * A token node is a node that essentially represents a concrete token and/or
 * that may be part of a list.
 *
 * It stores the comments and whitespaces that may come *before* it, and an
 * optional separator token that may be used *after* it to separate it from
 * the next token in an underlying list.
 *
 * The separator remains null when the node is not part of a list, or not part
 * of a list that uses separators.
 *
 * A token node is a wrapped node if its openToken and closeToken properties
 * are set.
 */
export class TokenNode extends WrappedNode {
    // Comments and whitespace *before* the node
    comments = [];
    whitespace = "";
    separator = null;
    /**
     * Function may be useful in subclasses to output something
     * before the comments and whitespace associated with the
     * main token
     */
    _prestr(marker) {
        return "";
    }
    serialize(marker) {
        let output = this._prestr(marker);
        for (const comment of this.comments) {
            output += this._serializeToken(comment, marker);
        }
        output += this.whitespace;
        output += super.serialize(marker);
        output += this._serializeToken(this.separator, marker);
        return output;
    }
    setComments(token) {
        this.comments = token.comments;
        this.whitespace = token.whitespace;
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const comment of this.comments) {
            res.push(comment.toString(indent + 1));
        }
        if (this.whitespace !== "") {
            res.push("  ".repeat(indent + 1) + `whitespaces: ${this.whitespace.length}`);
        }
        if (this.separator) {
            res.push(this.separator.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            comments: this.comments.map(c => c.toJSON()),
            whitespace: this.whitespace,
            separator: this.separator?.toJSON()
        });
    }
}
/**
 * Represents a set of CDDL rules
 */
export class CDDLTree extends TokenNode {
    rules;
    constructor(rules) {
        super();
        this.rules = rules;
    }
    getChildren() {
        return this.rules;
    }
    _serialize(marker) {
        return this.rules.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const rule of this.rules) {
            res.push(rule.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            rules: this.rules.map(r => r.toJSON())
        });
    }
}
/**
 * A group definition
 */
export class Rule extends CDDLNode {
    name;
    assign;
    type;
    constructor(name, assign, type) {
        super();
        this.name = name;
        this.assign = assign;
        this.type = type;
    }
    getChildren() {
        return [this.name, this.type];
    }
    _serialize(marker) {
        let output = this.name.serialize(marker);
        output += this._serializeToken(this.assign, marker);
        output += this.type.serialize(marker);
        return output;
    }
    toString(indent = 0) {
        return [
            super.toString(indent),
            this.name.toString(indent + 1),
            this.assign.toString(indent + 1),
            this.type.toString(indent + 1),
        ].join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            name: this.name.toJSON(),
            assign: this.assign.toJSON(),
            type: this.type.toJSON()
        });
    }
}
/**
 * A group entry
 */
export class GroupEntry extends TokenNode {
    occurrence;
    key;
    type;
    constructor(occurrence, key, type) {
        super();
        this.occurrence = occurrence;
        this.key = key;
        this.type = type;
    }
    getChildren() {
        const children = [];
        if (this.occurrence) {
            children.push(this.occurrence);
        }
        if (this.key) {
            children.push(this.key);
        }
        children.push(this.type);
        return children;
    }
    _serialize(marker) {
        let output = "";
        if (this.occurrence) {
            output += this.occurrence.serialize(marker);
        }
        if (this.key) {
            output += this.key.serialize(marker);
        }
        output += this.type.serialize(marker);
        return output;
    }
    /**
     * Return true if GroupEntry can be converted to a proper Type.
     */
    isConvertibleToType() {
        return (this.occurrence === null &&
            this.key === null &&
            (!(this.type instanceof Group) ||
                this.type instanceof Array ||
                this.type instanceof Map));
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        if (this.occurrence) {
            res.push(this.occurrence.toString(indent + 1));
        }
        if (this.key) {
            res.push(this.key.toString(indent + 1));
        }
        res.push(this.type.toString(indent + 1));
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            occurrence: this.occurrence?.toJSON(),
            key: this.key?.toJSON(),
            type: this.type.toJSON()
        });
    }
}
/**
 * A group, meaning a list of group choices wrapped in parentheses
 */
export class Group extends TokenNode {
    groupChoices;
    constructor(groupChoices) {
        super();
        this.groupChoices = groupChoices;
    }
    getChildren() {
        return this.groupChoices;
    }
    _serialize(marker) {
        return this.groupChoices.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.groupChoices) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            groupChoices: this.groupChoices.map(g => g.toJSON())
        });
    }
}
/**
 * A map, meaning a list of group choices wrapped in curly braces
 */
export class Map extends Group {
}
/**
 * An array
 */
export class Array extends Group {
}
/**
 * A group choice
 */
export class GroupChoice extends TokenNode {
    groupEntries;
    constructor(groupEntries) {
        super();
        this.groupEntries = groupEntries;
    }
    getChildren() {
        return this.groupEntries;
    }
    _serialize(marker) {
        return this.groupEntries.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.groupEntries) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            groupEntries: this.groupEntries.map(g => g.toJSON())
        });
    }
}
/**
 * A tag definition
 */
export class Tag extends TokenNode {
    numericPart;
    typePart;
    constructor(numericPart = null, typePart = null) {
        super();
        this.numericPart = numericPart;
        this.typePart = typePart;
    }
    getChildren() {
        return this.typePart ? [this.typePart] : [];
    }
    _serialize(marker) {
        let output = this._serializeToken(new Token(Tokens.HASH, ""), marker);
        output += this._serializeToken(this.numericPart, marker);
        output += this.typePart ? this.typePart.serialize(marker) : "";
        return output;
    }
    toString(indent = 0) {
        const res = [super.toString(indent) + " (#)"];
        if (this.numericPart) {
            res.push(this.numericPart.toString(indent + 1));
        }
        if (this.typePart) {
            res.push(this.typePart.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            numericPart: this.numericPart?.toJSON(),
            typePart: this.typePart?.toJSON()
        });
    }
}
/**
 * Occurrence
 */
export class Occurrence extends TokenNode {
    n;
    m;
    tokens;
    constructor(n, m, tokens = []) {
        super();
        this.n = n;
        this.m = m;
        this.tokens = tokens;
    }
    _serialize(marker) {
        return this.tokens.map((item) => this._serializeToken(item, marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.tokens) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            n: this.n,
            m: this.m,
            tokens: this.tokens.map(t => t.toJSON())
        });
    }
}
/**
 * A value (number, text or bytes)
 */
export class Value extends TokenNode {
    value;
    type;
    constructor(value, type) {
        super();
        this.value = value;
        this.type = type;
    }
    _serialize(marker) {
        let prefix = "";
        let suffix = "";
        if (this.type === "text") {
            prefix = '"';
            suffix = '"';
        }
        else if (this.type === "bytes") {
            prefix = "'";
            suffix = "'";
        }
        else if (this.type === "hex") {
            prefix = "h'";
            suffix = "'";
        }
        else if (this.type === "base64") {
            prefix = "b64'";
            suffix = "'";
        }
        if (!marker) {
            return prefix + this.value + suffix;
        }
        return marker.serializeValue(prefix, this.value, suffix, this);
    }
    toString(indent = 0) {
        return "  ".repeat(indent) + `${this.constructor.name} (${this.type}): ${this.value}`;
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            value: this.value,
            type: this.type
        });
    }
}
/**
 * A typename (or groupname)
 */
export class Typename extends TokenNode {
    name;
    unwrapped;
    parameters;
    constructor(name, unwrapped, parameters = null) {
        super();
        this.name = name;
        this.unwrapped = unwrapped;
        this.parameters = parameters;
    }
    getChildren() {
        return this.parameters ? [this.parameters] : [];
    }
    _prestr(marker) {
        return this._serializeToken(this.unwrapped, marker);
    }
    _serialize(marker) {
        let output = "";
        if (!marker) {
            output = this.name;
        }
        else {
            output += marker.serializeName(this.name, this);
        }
        if (this.parameters) {
            output += this.parameters.serialize(marker);
        }
        return output;
    }
    toString(indent = 0) {
        const res = [super.toString(indent), "  ".repeat(indent + 1) + this.name];
        if (this.unwrapped) {
            res.push(this.unwrapped.toString(indent + 1));
        }
        if (this.parameters) {
            res.push(this.parameters.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            name: this.name,
            unwrapped: this.unwrapped?.toJSON(),
            parameters: this.parameters?.toJSON()
        });
    }
}
/**
 * A choice built from a group (or a groupname)
 */
export class ChoiceFrom extends TokenNode {
    target;
    constructor(target) {
        super();
        this.target = target;
    }
    getChildren() {
        return [this.target];
    }
    _serialize(marker) {
        let output = this._serializeToken(new Token(Tokens.AMPERSAND, ""), marker);
        output += this.target.serialize(marker);
        return output;
    }
    toString(indent = 0) {
        const res = [
            super.toString(indent) + " (&)",
            this.target.toString(indent + 1),
        ];
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            target: this.target.toJSON()
        });
    }
}
/**
 * A Range is a specific kind of Type1.
 */
export class Range extends TokenNode {
    min;
    max;
    rangeop;
    constructor(min, max, rangeop) {
        super();
        this.min = min;
        this.max = max;
        this.rangeop = rangeop;
    }
    getChildren() {
        return [this.min, this.max];
    }
    _serialize(marker) {
        let output = this.min.serialize(marker);
        output += this._serializeToken(this.rangeop, marker);
        output += this.max.serialize(marker);
        return output;
    }
    toString(indent = 0) {
        const res = [
            super.toString(indent),
            this.min.toString(indent + 1),
            this.rangeop.toString(indent + 1),
            this.max.toString(indent + 1),
        ];
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            min: this.min.toJSON(),
            max: this.max.toJSON(),
            rangeop: this.rangeop.toJSON()
        });
    }
}
/**
 * An operator is a specific type of Type1
 */
export class Operator extends TokenNode {
    type;
    name;
    controller;
    constructor(type, name, controller) {
        super();
        this.type = type;
        this.name = name;
        this.controller = controller;
    }
    getChildren() {
        return [this.type, this.controller];
    }
    _serialize(marker) {
        let output = this.type.serialize(marker);
        output += this._serializeToken(this.name, marker);
        output += this.controller.serialize(marker);
        return output;
    }
    toString(indent = 0) {
        const res = [
            super.toString(indent),
            this.type.toString(indent + 1),
            this.name.toString(indent + 1),
            this.controller.toString(indent + 1),
        ];
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            type: this.type.toJSON(),
            name: this.name.toJSON(),
            controller: this.controller.toJSON()
        });
    }
}
/**
 * Memberkey
 */
export class Memberkey extends CDDLNode {
    type;
    hasCut;
    hasColon;
    tokens;
    constructor(type, hasCut, hasColon, tokens = []) {
        super();
        this.type = type;
        this.hasCut = hasCut;
        this.hasColon = hasColon;
        this.tokens = tokens;
    }
    getChildren() {
        return [this.type];
    }
    _serialize(marker) {
        let output = this.type.serialize(marker);
        output += this.tokens.map((token) => this._serializeToken(token, marker)).join("");
        return output;
    }
    toString(indent = 0) {
        const res = [super.toString(indent), this.type.toString(indent + 1)];
        for (const item of this.tokens) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            type: this.type.toJSON(),
            hasCut: this.hasCut,
            hasColon: this.hasColon,
            tokens: this.tokens.map(t => t.toJSON())
        });
    }
}
/**
 * A Type is a list of Type1, each representing a possible choice.
 */
export class Type extends TokenNode {
    types;
    constructor(types) {
        super();
        this.types = types;
    }
    getChildren() {
        return this.types;
    }
    _serialize(marker) {
        return this.types.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.types) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            types: this.types.map(t => t.toJSON())
        });
    }
}
/**
 * A set of generic parameters
 */
export class GenericParameters extends WrappedNode {
    parameters;
    constructor(parameters) {
        super();
        this.parameters = parameters;
    }
    getChildren() {
        return this.parameters;
    }
    _serialize(marker) {
        return this.parameters.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.parameters) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            parameters: this.parameters.map(p => p.toJSON())
        });
    }
}
/**
 * A set of generic arguments
 */
export class GenericArguments extends WrappedNode {
    parameters;
    constructor(parameters) {
        super();
        this.parameters = parameters;
    }
    getChildren() {
        return this.parameters;
    }
    _serialize(marker) {
        return this.parameters.map((item) => item.serialize(marker)).join("");
    }
    toString(indent = 0) {
        const res = [super.toString(indent)];
        for (const item of this.parameters) {
            res.push(item.toString(indent + 1));
        }
        return res.join("\n");
    }
    toJSON() {
        return Object.assign(super.toJSON(), {
            parameters: this.parameters.map(p => p.toJSON())
        });
    }
}
//# sourceMappingURL=ast.js.map